import type {
  Catalogs,
  HealthResponse,
  Me,
  OccurrenceRow,
  OccurrencesQuery,
  OccurrencesResponse,
  PatchOccurrenceBody,
  RecurrencesQuery,
  RecurrencesResponse,
  RequestDetail,
  RequestsQuery,
  RequestsResponse,
  TimePerPersonQuery,
  TimePerPersonResponse,
} from "@shared/api"
import { getSupabase } from "../config/supabaseClient"

/**
 * Cliente tipado de /api/dashboard/*.
 *
 * Todos los tipos vienen de @shared/api, que es el MISMO fuente que compila el
 * servidor (src/shared/api.ts). No hay codegen ni duplicación: si alguien agrega una
 * columna o un activity_type, las dos puntas se enteran en el mismo commit.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message)
    this.name = "ApiError"
  }
}

/**
 * Refresh SINGLE-FLIGHT compartido entre callers concurrentes: cinco requests en
 * paralelo que reciben 401 disparan UN refresh, no cinco (cinco refreshes en paralelo
 * se invalidan entre sí y terminan cerrando la sesión).
 */
let refreshing: Promise<boolean> | null = null

async function refreshOnce(): Promise<boolean> {
  if (!refreshing) {
    refreshing = getSupabase()
      .auth.refreshSession()
      .then(({ data, error }) => !error && !!data.session)
      .catch(() => false)
      .finally(() => {
        refreshing = null
      })
  }
  return refreshing
}

/** Se pone en true cuando un 401 sobrevive al refresh, para que SignIn explique por qué. */
export let sessionExpired = false

async function request<T>(path: string, init: RequestInit = {}, retried = false): Promise<T> {
  // getSession() devuelve la sesión persistida y la refresca sola si expiró. NUNCA
  // leer el token de localStorage a mano ni cachearlo en una variable de módulo: se
  // quedaría viejo justo después de un refresh automático.
  const { data } = await getSupabase().auth.getSession()
  const token = data.session?.access_token
  if (!token) throw new ApiError(401, "no_session", "No hay sesión activa.")

  const headers = new Headers(init.headers)
  headers.set("Authorization", `Bearer ${token}`)
  headers.set("Accept", "application/json")
  if (init.body !== undefined) headers.set("Content-Type", "application/json")

  const res = await fetch(`/api/dashboard${path}`, { ...init, headers })

  if (res.status === 401 && !retried) {
    if (await refreshOnce()) return request<T>(path, init, true)
    sessionExpired = true
    await getSupabase().auth.signOut()
    throw new ApiError(401, "session_expired", "Tu sesión expiró. Volvé a entrar.")
  }

  if (res.status === 204) return undefined as T

  let body: { error?: string; message?: string } | null = null
  let parsed: unknown = null
  try {
    parsed = await res.json()
    body = parsed as { error?: string; message?: string }
  } catch {
    // Un 502 de proxy devuelve HTML, no JSON: se cae al statusText.
  }

  if (!res.ok) {
    // 403 NO cierra sesión: el usuario está autenticado, simplemente no es admin.
    // App.tsx lo traduce a la pantalla <NoAccess/>.
    throw new ApiError(
      res.status,
      body?.error ?? String(res.status),
      body?.message ?? res.statusText ?? "Error inesperado."
    )
  }

  return parsed as T
}

function toSearchParams(q: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue
    if (Array.isArray(v)) {
      if (v.length > 0) p.set(k, v.join(","))
    } else {
      p.set(k, String(v))
    }
  }
  const s = p.toString()
  return s ? `?${s}` : ""
}

export const api = {
  me: (signal?: AbortSignal) => request<Me>("/me", { signal }),
  catalogs: (signal?: AbortSignal) => request<Catalogs>("/catalogs", { signal }),
  people: (signal?: AbortSignal) =>
    request<{ rows: { slack_user_id: string; name: string }[] }>("/people", { signal }),
  requests: {
    list: (q: RequestsQuery, signal?: AbortSignal) =>
      request<RequestsResponse>(`/requests${toSearchParams(q as Record<string, unknown>)}`, {
        signal,
      }),
    detail: (id: string, signal?: AbortSignal) =>
      request<RequestDetail>(`/requests/${encodeURIComponent(id)}`, { signal }),
  },
  timePerPerson: (q: TimePerPersonQuery, signal?: AbortSignal) =>
    request<TimePerPersonResponse>(
      `/time-per-person${toSearchParams(q as unknown as Record<string, unknown>)}`,
      { signal }
    ),
  // OJO: /api/dashboard/health (JSON, con auth) no es el /health del probe de DO
  // (texto plano, sin auth) — src/http.ts monta los dos, en paths distintos.
  health: (signal?: AbortSignal) => request<HealthResponse>("/health", { signal }),
  occurrences: {
    list: (q: OccurrencesQuery, signal?: AbortSignal) =>
      request<OccurrencesResponse>(
        `/occurrences${toSearchParams(q as unknown as Record<string, unknown>)}`,
        { signal }
      ),
    /**
     * LA ÚNICA ESCRITURA DE TODO EL SPA.
     *
     * Se manda EXACTAMENTE lo que cambió, nunca la fila entera: el servidor rechaza
     * cualquier clave que no sea status o notes (`unknown_field`), así que mandar el
     * row completo sería un 400. Es a propósito — ver parseOccurrencePatch en src/api.ts.
     *
     * Sin `signal`: un guardado a medio camino no se cancela por desmontar la fila.
     * Abortarlo dejaría al admin sin saber si guardó.
     */
    patch: (id: string, patch: PatchOccurrenceBody) =>
      request<OccurrenceRow>(`/occurrences/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },
  recurrences: {
    list: (q: RecurrencesQuery, signal?: AbortSignal) =>
      request<RecurrencesResponse>(
        `/recurrences${toSearchParams(q as unknown as Record<string, unknown>)}`,
        { signal }
      ),
  },
}
